'use client';

import {
  Autocomplete,
  Box,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';

import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { StepErrorDialog } from '@/components/dialogs/stepErrorDialog';
import { CopyableCode } from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { Regions } from '@/data/inventory/regions';

import {
  getAtlasEndpointServiceBashCode,
  getAtlasEndpointServiceTerraformCode,
} from '../mongoAtlasPrivateLinkCode';
import type { MongoAtlasPrivateLinkWizardState } from '../mongoAtlasPrivateLinkWizardState';

export const SetupAtlasPrivateLink = (
  props: StepProps<MongoAtlasPrivateLinkWizardState>
) => {
  const { wizardState, setWizardState } = props;
  const [isDeploying, setIsDeploying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const dal = useDAL();

  const {
    setupMethod,
    atlasProjectId,
    cloudAccountId,
    region,
    serviceName,
    resume,
  } = wizardState;

  // If resuming an existing setup, skip directly to the complete step
  useEffect(() => {
    if (resume) {
      props.setNextStep(props.currentStep.next?.[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const awsRegions = Regions[Provider.Aws];
  const regionOptions = useMemo(
    () =>
      Object.entries(awsRegions).map(([key, val]) => ({
        id: key,
        label: `${key} — ${val.displayName}`,
      })),
    [awsRegions]
  );

  // In UI mode, extract region from service name (com.amazonaws.vpce.{region}.vpce-svc-...)
  const extractedRegion = useMemo(() => {
    const match = /^com\.amazonaws\.vpce\.([^.]+)\./.exec(serviceName);
    return match?.[1] || '';
  }, [serviceName]);

  // Auto-select region dropdown when extracted from service name
  useEffect(() => {
    if (extractedRegion && extractedRegion !== region) {
      setWizardState((prev) => ({ ...prev, region: extractedRegion }));
    }
  }, [extractedRegion]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveRegion = setupMethod === 'ui' ? extractedRegion : region;

  const isMissingCodeFields = !atlasProjectId || !effectiveRegion;

  const code =
    setupMethod === 'terraform'
      ? getAtlasEndpointServiceTerraformCode({ atlasProjectId, region })
      : getAtlasEndpointServiceBashCode({ atlasProjectId, region });

  const codeTitle =
    setupMethod === 'terraform' ? 'main.tf' : 'create-atlas-endpoint.sh';

  const canGoNext = () =>
    !isDeploying &&
    effectiveRegion.trim().length > 0 &&
    serviceName.trim().length > 0;

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);

    try {
      await dal.mongoAtlasPrivateLinks.setup({
        cloudAccountId,
        region: effectiveRegion,
        serviceName,
      });
      // Save the resolved region back to wizard state so the next step can use it
      setWizardState((prev) => ({ ...prev, region: effectiveRegion }));
      props.setNextStep(props.currentStep.next?.[0]);
    } catch (err: unknown) {
      const errorMessage = await extractErrorMessage(err);
      setError(new Error(errorMessage));
    } finally {
      setIsDeploying(false);
    }
  };

  const bulletTitle =
    setupMethod === 'ui'
      ? 'Follow the Atlas UI steps to create the endpoint service, then paste the service name below. The region will be detected automatically.'
      : `Select a region, ${
          setupMethod === 'terraform'
            ? 'apply the generated Terraform module'
            : 'run the generated Bash script'
        } using your own Atlas API key with Project Owner permissions, then paste the service name from the output below.`;

  return (
    <>
      <StepContainer
        stepperIndex={0}
        hideStepper
        stepperLabels={props.stepperLabels}
        canGoNext={canGoNext}
        nextButtonDisabledTooltip={
          isDeploying
            ? 'Please wait for the setup to complete'
            : 'Please fill all required fields'
        }
        nextButtonText='Create PrivateLink'
        backButtonText='Cancel'
        onBackClick={props.back}
        sx={{ overflow: 'auto', paddingX: '0px', paddingTop: '24px' }}
        onNextClick={onNextClick}
      >
        <Stack divider={<Divider />} gap='24px'>
          <Stack paddingX='40px' direction='row' justifyContent='space-between'>
            <Typography width='540px' variant='body1'>
              Create a PrivateLink endpoint service on the Atlas side using your
              own Atlas API key with Project Owner permissions. Eon will then
              create a VPC endpoint to connect to it.
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={setupMethod}
              onChange={(_, value) => {
                if (value === null) return;
                setWizardState({ ...wizardState, setupMethod: value });
              }}
            >
              <ToggleButton value='ui'>UI</ToggleButton>
              <ToggleButton value='terraform'>Terraform</ToggleButton>
              <ToggleButton value='bash'>Bash</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Stack gap='24px' divider={<Divider />}>
            <Box padding='16px min(100px,6vw) 36px' width='100%' height='auto'>
              <BulletItem
                title={bulletTitle}
                leftContent={
                  <Stack
                    gap='24px'
                    divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                  >
                    {setupMethod !== 'ui' && (
                      <Stack gap='24px'>
                        <Autocomplete
                          options={regionOptions}
                          value={
                            regionOptions.find((r) => r.id === region) || null
                          }
                          onChange={(_, newValue) =>
                            setWizardState({
                              ...wizardState,
                              region: newValue?.id || '',
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label='AWS Region'
                              placeholder='Select a region'
                              size='small'
                            />
                          )}
                          disabled={isDeploying}
                          fullWidth
                          size='small'
                        />
                      </Stack>
                    )}
                    {setupMethod === 'ui' && (
                      <Stack gap='8px'>
                        <Typography variant='body2'>
                          1. Open the{' '}
                          <strong>
                            <a
                              href={`https://cloud.mongodb.com/v2/${atlasProjectId}#/security/network/privateEndpoint`}
                              target='_blank'
                              rel='noopener noreferrer'
                              style={{ textDecoration: 'underline' }}
                            >
                              Private Endpoint page
                            </a>
                          </strong>{' '}
                          in MongoDB Atlas.
                        </Typography>
                        <Typography variant='body2'>
                          2. Click <strong>Add Private Endpoint</strong> and
                          select <strong>AWS</strong>.
                        </Typography>
                        <Typography variant='body2'>
                          3. Select your region and click <strong>Next</strong>.
                        </Typography>
                        <Typography variant='body2'>
                          4. Atlas will ask for a VPC ID and Subnet ID —{' '}
                          <strong>skip this step</strong>, Eon will create the
                          VPC endpoint for you.
                        </Typography>
                        <Typography variant='body2'>
                          5. Copy the <strong>Service Name</strong> and paste it
                          below.
                        </Typography>
                        {extractedRegion && (
                          <Typography variant='body2' color='text.secondary'>
                            Detected region: <strong>{extractedRegion}</strong>
                          </Typography>
                        )}
                      </Stack>
                    )}
                    {setupMethod !== 'ui' && (
                      <Typography variant='body2' color='text.secondary'>
                        After running the setup, copy the{' '}
                        <strong>service name</strong> from the output and paste
                        it below.
                      </Typography>
                    )}
                    <TextField
                      label='Atlas endpoint service name'
                      placeholder='com.amazonaws.vpce.us-east-1.vpce-svc-...'
                      value={serviceName}
                      onChange={(e) =>
                        setWizardState({
                          ...wizardState,
                          serviceName: e.target.value,
                        })
                      }
                      fullWidth
                      size='small'
                      disabled={isDeploying}
                    />
                  </Stack>
                }
                rightContent={
                  setupMethod !== 'ui' ? (
                    <CopyableCode
                      title={codeTitle}
                      code={code}
                      blur={isMissingCodeFields}
                    />
                  ) : undefined
                }
              />
            </Box>
          </Stack>
        </Stack>
      </StepContainer>
      <StepErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
